import { useContext, useEffect, useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import moment from "moment";
import { API, graphqlOperation } from "aws-amplify";
import {
  Button,
  Card,
  Grid,
  ScrollView,
  SelectField,
  TextField,
  Loader,
} from "@aws-amplify/ui-react";
import AlertIcon from "@mui/icons-material/NotificationImportant";
import {
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTooltip,
} from "@coreui/react";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import {
  alertLogsByBranchID,
  alertLogsByCompanyID,
  alertLogsByDeviceID,
  alertLogsByStatus,
  branchesByCompanyID,
  devicesByBranchID,
  listAlertLogs,
  listCompanies,
} from "graphql/queries";
import { globalStrings } from "config/translations/es";
import Empty from "components/utils/Empty";
import { updateAlertLog } from "graphql/mutations";
import { LayoutContext } from "layout/context/LayoutContext";
import DateRangeForm from "components/utils/DateRangeForm";
import CompaniesTree from "components/companies/CompaniesTree";
import DateRangeInput from "components/utils/DateRangeInput";
import AlertCard from "components/alerts/AlertCard";

const AlertTreeIcon = ({ item }) => (
  <CTooltip
    content={
      <div>
        <p>
          {item.name} / {globalStrings.severity.options[item.severity]}
        </p>
        <p style={{ marginBottom: 0 }}>{item.description}</p>
      </div>
    }
  >
    <AlertIcon style={{ fontSize: "20px" }} htmlColor={item.severityColor} />
  </CTooltip>
);

const AlertLogs = () => {
  const { me } = useContext(LayoutContext);
  const [modalDate, setModalDate] = useState({
    openBy: "",
    visible: false,
  });
  const [filters, setFilters] = useState({
    status: "",
    user: "",
    activatedAt: {
      start: "",
      end: "",
    },
    recognizedAt: {
      start: "",
      end: "",
    },
  });
  const [tree, setTree] = useState([]);
  const [filterTree, setFilterTree] = useState("");
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [nextToken, setNextToken] = useState();
  const [selectedItem, setSelectedItem] = useState({
    queryBy: "all",
    id: "",
  });
  const treeMapped = useMemo(() => {
    return tree
      ?.filter((item) =>
        filterTree
          ? item.name.toLowerCase().includes(filterTree.toLowerCase()) ||
            item.branches?.items?.some(
              (branchItem) =>
                branchItem.name
                  .toLowerCase()
                  .includes(filterTree.toLowerCase()) ||
                branchItem.devices?.items?.some((deviceItem) =>
                  deviceItem.name
                    .toLowerCase()
                    .includes(filterTree.toLowerCase())
                )
            )
          : true
      )
      ?.map((item) => {
        const alertsFoundByCompany =
          activeAlerts?.filter((alert) => alert.companyID === item.id) ?? [];
        return {
          ...item,
          alerts: alertsFoundByCompany?.map((alertItem) => (
            <AlertTreeIcon item={alertItem} />
          )),
          branches: item.branches?.items?.map((branchItem) => {
            const alertsFoundByBranch =
              activeAlerts?.filter(
                (alert) => alert.branchID === branchItem.id
              ) ?? [];
            return {
              ...branchItem,
              alerts: alertsFoundByBranch?.map((alertItem) => (
                <AlertTreeIcon item={alertItem} />
              )),
              devices: branchItem.devices?.items?.map((deviceItem) => {
                const alertsFoundByDevice =
                  activeAlerts?.filter(
                    (alert) => alert.deviceID === deviceItem.id
                  ) ?? [];
                return {
                  ...deviceItem,
                  alerts: alertsFoundByDevice?.map((alertItem) => (
                    <AlertTreeIcon item={alertItem} />
                  )),
                };
              }),
            };
          }),
        };
      });
  }, [tree, activeAlerts, filterTree]);

  const onSelectItem = ({ queryBy, id }, companyID) => {
    if (selectedItem.queryBy === queryBy && selectedItem.id === id) return;
    setSelectedItem({ queryBy, id });
    if (
      queryBy === "companyID" &&
      tree?.some((prevTree) => prevTree.id === id && !prevTree.branches?.items)
    )
      fetchTreeBranches(id);
    if (
      queryBy === "branchID" &&
      tree?.some(
        (prevTree) =>
          prevTree.id === companyID &&
          prevTree.branches?.items?.some(
            (prevBranch) => prevBranch.id === id && !prevBranch.devices?.items
          )
      )
    )
      fetchTreeDevices(id, companyID);
  };

  const onLoadMore = () => {
    fetchAlertsWithFilters(filters, selectedItem, nextToken);
  };

  const onSubmitDateRange = (name, start, end) => {
    setFilters((prevSt) => ({
      ...prevSt,
      [name]: {
        start,
        end,
      },
    }));
    setModalDate({ openBy: "", visible: false });
  };

  const onChangeFilter = (e) => {
    const { name, value } = e.target;
    setFilters((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const onClearDate = (name) => {
    setFilters((prevSt) => ({
      ...prevSt,
      [name]: {
        start: "",
        end: "",
      },
    }));
  };

  async function alertRecognize(id) {
    try {
      const newData = {
        status: "2",
        recognizedAt: new Date().toISOString(),
        recognizedByUser: me.email,
      };
      await API.graphql(
        graphqlOperation(updateAlertLog, { input: { id, ...newData } })
      );
      toast.success(strings.successRecognized);
      setAlerts((list) =>
        list.map((item) => (item.id === id ? { ...item, ...newData } : item))
      );
      setActiveAlerts((list) => list.filter((item) => item.id !== id));
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  async function fetchAlerts(operation, operationName, operationObj) {
    try {
      setAlertsLoading(true);
      const alertsData = await API.graphql(
        graphqlOperation(operation, operationObj)
      );
      const alertsItems = alertsData.data[operationName].items ?? [];
      if (operationObj?.nextToken)
        setAlerts((prevAlerts) => [
          ...prevAlerts,
          ...alertsItems.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          ),
        ]);
      else
        setAlerts(
          alertsItems.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
        );
      setNextToken(alertsData.data[operationName].nextToken);
      setAlertsLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setAlertsLoading(false);
    }
  }

  async function fetchActiveAlerts() {
    let filterObj = {};
    try {
      if (me.companyID)
        filterObj = { ...filterObj, or: [{ companyID: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ companyID: { eq: cp } })),
          ],
        };
      const alertsData = await API.graphql(
        graphqlOperation(alertLogsByStatus, {
          status: "1",
          filter: {
            ...filterObj,
          },
        })
      );
      const alertsItems = alertsData.data.alertLogsByStatus.items ?? [];
      setActiveAlerts(
        alertsItems.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      );
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  async function fetchTree() {
    let filterObj = {};
    try {
      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit: Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setTree(companiesItems);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  async function fetchTreeBranches(companyID) {
    try {
      const treeData = await API.graphql(
        graphqlOperation(branchesByCompanyID, { companyID })
      );
      const treeItems = treeData.data.branchesByCompanyID.items;
      setTree((prevTree) =>
        prevTree?.map((prevItem) =>
          prevItem.id === companyID
            ? { ...prevItem, branches: { items: treeItems } }
            : prevItem
        )
      );
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  async function fetchTreeDevices(branchID, companyID) {
    try {
      const treeData = await API.graphql(
        graphqlOperation(devicesByBranchID, { branchID })
      );
      const treeItems = treeData.data.devicesByBranchID.items;
      setTree((prevTree) =>
        prevTree?.map((prevItem) =>
          prevItem.id === companyID
            ? {
                ...prevItem,
                branches: {
                  items: prevItem.branches?.items?.map((prevBranchItem) =>
                    prevBranchItem.id === branchID
                      ? { ...prevBranchItem, devices: { items: treeItems } }
                      : prevBranchItem
                  ),
                },
              }
            : prevItem
        )
      );
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  const fetchAlertsWithFilters = (filters, selectedItem, nextTokenParam) => {
    let operation = undefined;
    let operationName = undefined;
    let operationObj = undefined;

    if (selectedItem.queryBy === "all") {
      operation = listAlertLogs;
      operationName = "listAlertLogs";
      if (me.companyID)
        operationObj = {
          filter: { or: [{ companyID: { eq: me.companyID } }] },
        };
      if (me.managedCompanies.length > 0)
        operationObj = {
          ...operationObj,
          filter: {
            ...operationObj?.filter,
            or: [
              ...(operationObj?.filter?.or ?? []),
              ...me.managedCompanies.map((cp) => ({ companyID: { eq: cp } })),
            ],
          },
        };
    } else if (selectedItem.queryBy === "companyID") {
      operation = alertLogsByCompanyID;
      operationName = "alertLogsByCompanyID";
      operationObj = {
        companyID: selectedItem.id,
      };
    } else if (selectedItem.queryBy === "branchID") {
      operation = alertLogsByBranchID;
      operationName = "alertLogsByBranchID";
      operationObj = {
        branchID: selectedItem.id,
      };
    } else if (selectedItem.queryBy === "deviceID") {
      operation = alertLogsByDeviceID;
      operationName = "alertLogsByDeviceID";
      operationObj = {
        deviceID: selectedItem.id,
      };
    }

    if (filters.status)
      operationObj = {
        ...operationObj,
        filter: {
          ...operationObj?.filter,
          status: { eq: filters.status },
        },
      };
    if (filters.user)
      operationObj = {
        ...operationObj,
        filter: {
          ...operationObj?.filter,
          recognizedByUser: { contains: filters.user },
        },
      };
    if (filters.activatedAt.start)
      operationObj = {
        ...operationObj,
        filter: {
          ...operationObj?.filter,
          createdAt: {
            ge: moment(filters.activatedAt.start).startOf("day").toISOString(),
            le: moment(filters.activatedAt.end).endOf("day").toISOString(),
          },
        },
      };
    if (filters.recognizedAt.start)
      operationObj = {
        ...operationObj,
        filter: {
          ...operationObj?.filter,
          recognizedAt: {
            ge: moment(filters.recognizedAt.start).startOf("day").toISOString(),
            le: moment(filters.recognizedAt.end).endOf("day").toISOString(),
          },
        },
      };
    if (!operationObj?.hasOwnProperty("filter"))
      operationObj = {
        ...operationObj,
        limit: 10,
      };
    if (nextTokenParam)
      operationObj = {
        ...operationObj,
        nextToken: nextTokenParam,
      };

    fetchAlerts(operation, operationName, operationObj);
  };

  useEffect(() => {
    fetchTree();
    fetchActiveAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAlertsWithFilters(filters, selectedItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedItem]);

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonRefresh}
        variation="link"
        onClick={() => fetchAlertsWithFilters(filters, selectedItem)}
      >
        <AutorenewIcon /> {strings.refresh}
      </Button>
      <Grid className="mt-3" gap={20} templateColumns={{ medium: "1fr 3fr" }}>
        <Card borderRadius="medium" variation="outlined">
          <CompaniesTree
            tree={treeMapped}
            onSelectAll={() => onSelectItem({ queryBy: "all" })}
            onSelectCompany={(id) => onSelectItem({ queryBy: "companyID", id })}
            onSelectBranch={(id, companyID) =>
              onSelectItem({ queryBy: "branchID", id }, companyID)
            }
            onSelectDevice={(id) => onSelectItem({ queryBy: "deviceID", id })}
            onSearchSubmit={setFilterTree}
          />
        </Card>
        <Grid height="fit-content" gap={10} templateColumns={{ medium: "1fr" }}>
          <Grid
            className="mb-3"
            gap={20}
            templateColumns={{ medium: "1fr 1fr" }}
          >
            <SelectField
              name="status"
              value={filters.status}
              label={strings.filters.status}
              onChange={onChangeFilter}
            >
              <option value="">{globalStrings.selectOption}</option>
              <option value="1">{strings.status.activated}</option>
              <option value="2">{strings.status.recognized}</option>
            </SelectField>
            <TextField
              name="user"
              label={strings.filters.user}
              value={filters.user}
              onChange={onChangeFilter}
            ></TextField>
            <DateRangeInput
              name="activatedAt"
              label={strings.filters.activatedAt}
              start={filters.activatedAt.start}
              end={filters.activatedAt.end}
              onClick={() =>
                setModalDate({ openBy: "activatedAt", visible: true })
              }
              onClear={() => onClearDate("activatedAt")}
            />
            <DateRangeInput
              name="recognizedAt"
              label={strings.filters.recognizedAt}
              start={filters.recognizedAt.start}
              end={filters.recognizedAt.end}
              onClick={() =>
                setModalDate({ openBy: "recognizedAt", visible: true })
              }
              onClear={() => onClearDate("recognizedAt")}
            />
          </Grid>
          {alertsLoading ? (
            <Loader />
          ) : alerts?.length <= 0 ? (
            <Empty />
          ) : (
            <>
              <ScrollView maxHeight="60vh">
                <Grid gap={20}>
                  {alerts?.map((alertItem) => (
                    <AlertCard
                      item={alertItem}
                      onRecognize={(id) => alertRecognize(id)}
                    />
                  ))}
                </Grid>
              </ScrollView>
              {nextToken && (
                <Button onClick={onLoadMore} variation="link">
                  {strings.loadMore}
                </Button>
              )}
            </>
          )}
        </Grid>
      </Grid>
      <CModal
        alignment="center"
        visible={modalDate.visible}
        onClose={() => setModalDate({ openBy: "", visible: false })}
        portal={false}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>{strings.selectDateRange}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <DateRangeForm
            onSubmit={({ from, to }) =>
              onSubmitDateRange(modalDate.openBy, from, to)
            }
          />
        </CModalBody>
      </CModal>
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Alertas",
    search: {
      placeholder: "Buscar...",
      noResults: "No hay resultados",
    },
    responses: {
      errorFetch: "Error al consultar datos",
    },
    companies: "Empresas",
    recognize: "Reconocer",
    activatedAt: "Fecha activado",
    recognizedAt: "Fecha reconocido",
    successRecognized: "Alerta reconocida con éxito",
    filters: {
      status: "Estado",
      user: "Usuario quien reconoce",
      activatedAt: "Fecha activado",
      recognizedAt: "Fecha reconocido",
    },
    status: {
      activated: "Activado",
      recognized: "Reconocido",
    },
    selectDateRange: "Seleccionar Rango de Fecha",
    loadMore: "Cargar más...",
    refresh: "Refrescar",
  },
});

const styles = {
  buttonRefresh: { float: "right" },
};

export default AlertLogs;
